'use client'

import { cn } from '../cn'
import { ActionCard } from './action-card'

/**
 * DiffCard — before/after view (file edit, schedule change). Output card.
 *
 * Renders a simple line-by-line diff: removed lines from `before` get
 * a red left rule + strikethrough, added lines from `after` get a
 * green left rule. Line equality is naive (literal string match) — the
 * goal is "show what changed" at-a-glance, not a full unified diff.
 * Tools that need true diff fidelity should pre-render and pass via
 * `bodyMarkdown` on a PreviewCard instead.
 *
 * Per `apps/agent/plans/plan-action-cards.md` Phase 4.
 */

export interface DiffCardProps {
  title: string
  description?: string
  attribution?: string
  before: string
  after: string
  /** Optional language label shown next to the attribution. */
  language?: string
}

interface DiffLine {
  kind: 'same' | 'removed' | 'added'
  text: string
}

function naiveLineDiff(before: string, after: string): DiffLine[] {
  const a = before.split('\n')
  const b = after.split('\n')
  // LCS-based diff. Quadratic, fine for chat-sized blobs (<2k lines).
  const m = a.length
  const n = b.length
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0))
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i]![j] = a[i] === b[j] ? (dp[i + 1]?.[j + 1] ?? 0) + 1 : Math.max(dp[i + 1]?.[j] ?? 0, dp[i]?.[j + 1] ?? 0)
    }
  }
  const out: DiffLine[] = []
  let i = 0
  let j = 0
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      out.push({ kind: 'same', text: a[i]! })
      i++
      j++
    } else if ((dp[i + 1]?.[j] ?? 0) >= (dp[i]?.[j + 1] ?? 0)) {
      out.push({ kind: 'removed', text: a[i]! })
      i++
    } else {
      out.push({ kind: 'added', text: b[j]! })
      j++
    }
  }
  while (i < m) {
    out.push({ kind: 'removed', text: a[i]! })
    i++
  }
  while (j < n) {
    out.push({ kind: 'added', text: b[j]! })
    j++
  }
  return out
}

export function DiffCard({ title, description, attribution, before, after, language }: DiffCardProps) {
  const lines = naiveLineDiff(before, after)
  const fullAttribution = language && attribution ? `${attribution} · ${language}` : (attribution ?? language)
  return (
    <ActionCard title={title} description={description} attribution={fullAttribution} tone="neutral">
      <div className="overflow-x-auto rounded-md border border-border/40 bg-background">
        <pre className="m-0 px-3 py-2 font-mono text-[12px] leading-relaxed">
          {lines.map((l, i) => (
            <div
              key={i}
              className={cn(
                'border-l-2 pl-2',
                l.kind === 'same' && 'border-transparent text-muted-foreground',
                l.kind === 'removed' && 'border-destructive/60 bg-destructive/5 text-destructive line-through',
                l.kind === 'added' && 'border-emerald-500/60 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300',
              )}>
              {l.kind === 'removed' ? '- ' : l.kind === 'added' ? '+ ' : '  '}
              {l.text}
            </div>
          ))}
        </pre>
      </div>
    </ActionCard>
  )
}
